import React, {
  ForwardRefExoticComponent,
  ReactElement,
  forwardRef,
  useImperativeHandle,
} from "react";

import { FieldFilterDefinition, Graphic } from "../../generated/gql/graphql";
import RetryError from "../errors/retry-error";
import NoResults from "../lists/no-results";
import SkeletonList from "../lists/skeleton-list";

import GraphicListItem from "./graphic-list-item";
import useGraphics from "./use-graphics";

export type GraphicListProps = {
  query: string;
  filters: FieldFilterDefinition[];
  gridSize: number;

  handleGraphicClick: (
    graphicId: string,
    width: number,
    height: number,
  ) => void;
};

export interface GraphicListRef extends ForwardRefExoticComponent<ReactElement> {
  refetch(): void;
}

// eslint-disable-next-line react/display-name
export const GraphicList = forwardRef<GraphicListRef, GraphicListProps>(
  (props, ref): ReactElement => {
    const { query, gridSize, filters, handleGraphicClick } = props;

    const { loading, data, error, refetch } = useGraphics({
      searchQuery: query,
      filters,
    });

    useImperativeHandle(
      ref,
      () =>
        ({
          refetch,
        }) as GraphicListRef,
    );

    const handleItemClick = (
      graphic: Pick<Graphic, "id"> & {
        asset: Pick<Graphic["asset"], "width" | "height">;
      },
    ): void => {
      handleGraphicClick(graphic.id, graphic.asset.width, graphic.asset.height);
    };

    if (loading) {
      return <SkeletonList gridSize={gridSize} />;
    }

    if (error) {
      return <RetryError error={error} />;
    }

    if (data?.graphics.length === 0) {
      return <NoResults />;
    }

    return (
      <div className="flex min-h-0 flex-1 flex-wrap gap-3 overflow-y-auto px-5 py-3">
        {data?.graphics.map((graphic) => (
          <GraphicListItem
            key={graphic.id}
            gridSize={gridSize}
            onClick={() => handleItemClick(graphic)}
            displayType={gridSize === 2 ? "full" : "auto"}
            graphic={graphic}
          />
        ))}
      </div>
    );
  },
);
